<template>
  <lf-modal :close="close">
    <template #content>
      <div class="mt-8 flex flex-1 flex-col items-center">
        <icon-base :icon="IconDeleteRound" :width="125" :height="125" />
        <lf-h1>
          {{
            offers.length > 1
              ? $t("DEALS.DEAL_PROGRESS.DELETE_OFFERS")
              : $t("DEALS.DEAL_PROGRESS.DELETE_OFFER")
          }}
        </lf-h1>
        <label class="text-headline">
          {{
            offers.length > 1
              ? $t("DEALS.DEAL_PROGRESS.CONFIRM_OFFERS_DELETE")
              : $t("DEALS.DEAL_PROGRESS.CONFIRM_OFFER_DELETE")
          }}
        </label>
        <div
          class="scrollable-menu flex max-h-60 w-full flex-col items-center overflow-scroll pb-4"
        >
          <div
            v-for="offer in offers"
            :key="offer.id"
            class="mt-8 flex w-2/3 justify-between rounded-sm p-3 shadow-md"
          >
            <label class="w-32">
              {{
                offer.funder?.full_name ??
                `${$t("COMMON.UNKNOWN")} ${$t("COMMON.FUNDER")}`
              }}
            </label>
            <div class="flex flex-1 justify-around space-x-5">
              <label class="flex flex-1">
                {{
                  offer.details.offer_template?.name ??
                  `${$t("COMMON.UNKNOWN")} ${$t("COMMON.PRODUCT")}`
                }}
              </label>
              <label for="">
                {{ getFormattedDate(offer.created_at || "") }}
              </label>
            </div>
          </div>
        </div>

        <div class="flex w-full justify-evenly px-24 py-10">
          <outline-button class="w-56" @click="close">
            {{ $t("COMMON.CANCEL") }}
          </outline-button>
          <color-button class="w-56" type="error" @click="deleteOffers">
            {{ deleteMessage }}
          </color-button>
        </div>
      </div>
    </template>
  </lf-modal>
</template>
<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";

import { formatDateCustom } from "@/helpers/formatting";
import { useNotification } from "@/hooks/notifications";

import IconDeleteRound from "@/components/icons/IconDeleteRound.vue";
import LfModal from "@/components/ui/Modal.vue";

import type { IOffer } from "@/models/funders";
import type { PropType } from "vue";

const props = defineProps({
  offers: {
    type: Array as PropType<IOffer[]>,
    required: true,
  },
  close: {
    type: Function as PropType<() => void>,
    required: true,
  },
});

const emit = defineEmits<{
  offerDeleted: [void];
}>();

const store = useStore();
const { showMessage } = useNotification();

const getFormattedDate = (date: string) =>
  formatDateCustom(date, "MMM dd, yyyy");

const deleteMessage = computed(
  () => `Delete the Offer${props.offers.length === 1 ? "" : "s"}`
);

const deleteOffers = async () => {
  await Promise.all(
    props.offers.map((offer) => {
      return store.dispatch("applications/deleteOffer", {
        offer,
      });
    })
  );
  await store.dispatch("applications/getOffers");

  const msgTitle = `${props.offers.length} Offer${
    props.offers.length == 1 ? "" : "s"
  } Deleted`;
  showMessage(msgTitle, "success");
  emit("offerDeleted");
  props.close();
};
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.scrollable-menu::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.scrollable-menu {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  scroll-behavior: smooth;
}
</style>
